import React, { useState, useEffect } from 'react';
import { FaCalendar, FaTimes } from 'react-icons/fa';
import { doc, updateDoc } from 'firebase/firestore'; // Import updateDoc
import { db } from '../../config/firebaseConfig';
import { toast } from 'react-toastify';
import styles from './EditIncomeModal.module.css'; 
import { useTheme } from '../../contexts/ThemeContext';

// Initial empty state matching the AddIncomeModal structure for consistency
const initialFormData = {
    BütçeAdı: '',
    BütçeKaynak: '',
    BütçeSahibi: 'Şirket', 
    BütçeTipi: 'Gelir', 
    BütçeKategorisi: '',
    GiderTipi: 'Gelir', 
    ParaBirimi: 'TRY', 
    KDV: '0', 
    BütçeMiktarı: '',
    BütçeTarihi: '',
    BeklenenÖdemeTarihi: '', 
    ÖdemeDurumu: 'Alındı' 
};

const EditIncomeModal = ({ isOpen, onClose, onSubmit, budgetData }) => { 
  const { isDarkMode } = useTheme();
  // Initialize form data with initial state or budgetData if provided
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when budgetData is available or changes
  useEffect(() => {
    if (budgetData) {
      // Ensure all fields from budgetData are mapped, provide defaults if missing
      const populatedData = { ...initialFormData }; // Start with defaults
      for (const key in initialFormData) {
          if (budgetData.hasOwnProperty(key)) {
             populatedData[key] = budgetData[key];
          }
      }
      setFormData(populatedData);
    } else {
      // Reset if budgetData becomes null (e.g., modal closed and reopened)
      setFormData(initialFormData);
    }
  }, [budgetData, isOpen]); // Re-run if budgetData changes or modal reopens

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!budgetData || !budgetData.id) {
        toast.error("Düzenlenecek gelir bulunamadı!");
        return;
    }
    setIsSubmitting(true);

    // Prepare data for Firestore update (exclude id, createdAt, etc.)
    const { id, createdAt, ...dataToUpdate } = formData;
    // Ensure type is set correctly even if form field was hidden
    dataToUpdate.GiderTipi = 'Gelir'; 
    dataToUpdate.BütçeTipi = 'Gelir';

    const budgetRef = doc(db, 'Budgets', budgetData.id);

    try {
      await updateDoc(budgetRef, dataToUpdate);

      toast.success('Gelir başarıyla güncellendi!');
      onSubmit(budgetData.id, dataToUpdate); // Pass ID and updated data back
      onClose(); // Close modal on success

    } catch (error) {
      console.error('Error updating income:', error);
      toast.error('Gelir güncellenirken bir hata oluştu!');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ESC key to close modal (remains the same)
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevent background scrolling (remains the same)
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen || !budgetData) return null; // Don't render if not open or no data

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Gelir Düzenle</h2> {/* Changed title */}
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close modal">
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Form fields are the same as AddIncomeModal, but values come from state */}
          <form onSubmit={handleSubmit}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Gelir Adı</label>
              <input
                type="text"
                name="BütçeAdı"
                value={formData.BütçeAdı || ''} // Use fallback for safety
                onChange={handleChange}
                className={styles.input}
                required
                placeholder="Gelir adını giriniz"
              />
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Gelir Kaynağı</label>
                <select
                  name="BütçeKaynak"
                  value={formData.BütçeKaynak || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="Satışlar">Satışlar</option>
                  <option value="Hizmetler">Hizmetler</option>
                  <option value="Yatırımlar">Yatırımlar</option>
                  <option value="Diğer">Diğer</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                 <label className={styles.label}>Gelir Kategorisi</label>
                <select
                  name="BütçeKategorisi"
                  value={formData.BütçeKategorisi || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">Lütfen Seçiniz</option>
                  <option value="Ana Gelir">Ana Gelir</option>
                  <option value="Yan Gelir">Yan Gelir</option>
                  <option value="Faiz Geliri">Faiz Geliri</option>
                </select>
              </div>
            </div>
            
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tutar</label>
                <input
                  type="number"
                  name="BütçeMiktarı"
                  value={formData.BütçeMiktarı || ''}
                  onChange={handleChange}
                  className={styles.input}
                  required
                  placeholder="Tutar giriniz"
                  step="0.01"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Para Birimi</label>
                <select
                  name="ParaBirimi"
                  value={formData.ParaBirimi || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="TRY">TRY</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Gelir Tarihi</label>
                <div className={styles.dateInputWrapper}>
                  <input
                    type="date"
                    name="BütçeTarihi"
                    value={formData.BütçeTarihi || ''}
                    onChange={handleChange}
                    className={styles.input}
                    required
                  />
                  <FaCalendar className={styles.calendarIcon} />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Durum</label>
                <select
                  name="ÖdemeDurumu"
                  value={formData.ÖdemeDurumu || ''}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="Alındı">Alındı</option>
                  <option value="Beklemede">Beklemede</option>
                </select>
              </div>
            </div>
            
            {/* Other fields like KDV, Sahibi, Tipi are handled implicitly or defaulted */}

            <div className={styles.buttonGroup}>
              <button type="button" className={`${styles.button} ${styles.cancelButton}`} onClick={onClose}>
                İptal
              </button>
              {/* Removed Reset Button for Edit Modal */}
              <button type="submit" className={`${styles.button} ${styles.submitButton}`} disabled={isSubmitting}>
                {isSubmitting ? 'Güncelleniyor...' : 'Gelir Güncelle'} {/* Changed text */}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditIncomeModal; 